import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button } from './button';
import { Card } from './card';
import { Badge } from './badge';
import {
  Upload,
  File,
  Image,
  FileText,
  Archive,
  X,
  Paperclip,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { sanitizeFilenameForAzure, validateAzureFilename, createFileWithSanitizedName } from '@/utils/fileUtils';

interface AttachmentFile extends File {
  id: string;
  preview?: string;
}

interface FileUploadProps {
  onFilesChange: (files: AttachmentFile[]) => void;
  maxFiles?: number;
  maxSize?: number; // in bytes
  acceptedFileTypes?: {
    [key: string]: string[];
  };
  className?: string;
}

const defaultAcceptedTypes = {
  'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/zip': ['.zip'],
  'application/x-rar-compressed': ['.rar'],
  'text/plain': ['.txt'],
};

export const FileUpload: React.FC<FileUploadProps> = ({
  onFilesChange,
  maxFiles = 5,
  maxSize = 10 * 1024 * 1024, // 10MB
  acceptedFileTypes = defaultAcceptedTypes,
  className,
}) => {
  const [files, setFiles] = useState<AttachmentFile[]>([]);
  const [errors, setErrors] = useState<string[]>([]);

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: Array<{ file: File; errors: Array<{ code: string; message: string }> }>) => {
      setErrors([]);
      const newErrors: string[] = [];

      // Handle rejected files
      rejectedFiles.forEach(({ file, errors }) => {
        errors.forEach((error: { code: string; message: string }) => {
          if (error.code === 'file-too-large') {
            newErrors.push(`${file.name} is too large. Max size is ${formatFileSize(maxSize)}`);
          } else if (error.code === 'file-invalid-type') {
            newErrors.push(`${file.name} type is not supported`);
          } else if (error.code === 'too-many-files') {
            newErrors.push(`Too many files. Maximum ${maxFiles} files allowed`);
          }
        });
      });

      // Process accepted files with filename sanitization
      const processedFiles: AttachmentFile[] = acceptedFiles.map((file) => {
        // Validate and sanitize filename
        const validation = validateAzureFilename(file.name);
        const sanitizedFilename = sanitizeFilenameForAzure(file.name);
        
        // If filename had issues, show warning
        if (!validation.isValid) {
          newErrors.push(`File "${file.name}" was renamed to "${sanitizedFilename}" for Azure compatibility`);
        }
        
        // Create attachment file with sanitized name
        const sanitizedFile = createFileWithSanitizedName(file, sanitizedFilename);
        const attachmentFile = Object.assign(sanitizedFile, {
          id: Math.random().toString(36).substring(2, 15)
        }) as AttachmentFile;
        
        // Create preview for images
        if (file.type.startsWith('image/')) {
          attachmentFile.preview = URL.createObjectURL(file);
        }
        
        return attachmentFile;
      });

      const updatedFiles = [...files, ...processedFiles].slice(0, maxFiles);
      setFiles(updatedFiles);
      onFilesChange(updatedFiles);
      setErrors(newErrors);
    },
    [files, maxFiles, maxSize, onFilesChange]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedFileTypes,
    maxSize,
    maxFiles: maxFiles - files.length,
  });

  const removeFile = (fileId: string) => {
    const updatedFiles = files.filter((file) => {
      if (file.id === fileId && file.preview) {
        URL.revokeObjectURL(file.preview);
      }
      return file.id !== fileId;
    });
    setFiles(updatedFiles);
    onFilesChange(updatedFiles);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) return <Image className="h-4 w-4" />;
    if (fileType === 'application/pdf') return <FileText className="h-4 w-4" />;
    if (fileType.includes('zip') || fileType.includes('rar')) return <Archive className="h-4 w-4" />;
    return <File className="h-4 w-4" />;
  };

  return (
    <div className={cn('space-y-4', className)}>
      {/* Dropzone */}
      <Card
        {...getRootProps()}
        className={cn(
          'border-2 border-dashed p-6 cursor-pointer transition-colors',
          isDragActive
            ? 'border-primary bg-primary/5'
            : 'border-muted-foreground/25 hover:border-primary/50',
          files.length >= maxFiles && 'pointer-events-none opacity-50'
        )}
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center justify-center text-center">
          <Upload className="h-8 w-8 text-muted-foreground mb-2" />
          {isDragActive ? (
            <p className="text-sm text-muted-foreground">Drop files here...</p>
          ) : (
            <div>
              <p className="text-sm text-muted-foreground mb-1">
                Drag & drop files here, or click to select
              </p>
              <p className="text-xs text-muted-foreground">
                Max {maxFiles} files, up to {formatFileSize(maxSize)} each
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Error messages */}
      {errors.length > 0 && (
        <div className="space-y-1">
          {errors.map((error, index) => (
            <p key={index} className="text-sm text-destructive">
              {error}
            </p>
          ))}
        </div>
      )}

      {/* File list */}
      {files.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Paperclip className="h-4 w-4" />
            Attachments ({files.length})
          </div>
          <div className="space-y-2">
            {files.map((file) => (
              <div
                key={file.id}
                className="flex items-center justify-between p-3 border rounded-lg bg-muted/50"
              >
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  {file.preview ? (
                    <img
                      src={file.preview}
                      alt={file.name}
                      className="h-8 w-8 rounded object-cover"
                    />
                  ) : (
                    <div className="flex-shrink-0 text-muted-foreground">
                      {getFileIcon(file.type)}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate max-w-lg text-wrap">{file.name}</p>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-xs">
                        {file.type.split('/')[1]?.toUpperCase() || 'FILE'}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {formatFileSize(file.size)}
                      </span>
                    </div>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeFile(file.id)}
                  className="flex-shrink-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
